
import React, { useEffect, useState } from 'react';

// Interface for core web vitals metrics
interface WebVitalsMetrics {
  lcp: number; // Largest Contentful Paint
  fid: number; // First Input Delay
  cls: number; // Cumulative Layout Shift
  ttfb: number; // Time To First Byte
  fcp: number; // First Contentful Paint
}

const PerformanceMonitor: React.FC = () => {
  const [metrics, setMetrics] = useState<WebVitalsMetrics>({
    lcp: 0,
    fid: 0,
    cls: 0,
    ttfb: 0,
    fcp: 0
  });
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    // Only run in development
    if (!import.meta.env.DEV) return;
    
    const updateMetrics = () => {
      if (window && (window as any).perfMetrics) {
        setMetrics((window as any).perfMetrics);
      }
    };
    
    // Update metrics every second
    const interval = setInterval(updateMetrics, 1000);
    
    // Set up keyboard shortcut (Ctrl+Shift+P) to toggle metrics visibility
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.ctrlKey && e.shiftKey && e.key === 'P') {
        e.preventDefault();
        setVisible(prev => !prev);
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    
    // Initial update
    updateMetrics();
    
    return () => {
      clearInterval(interval);
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  if (!visible || !import.meta.env.DEV) {
    return null;
  }

  // Helper function to format metric values
  const formatMetric = (value: number, unit: string) => {
    if (value === 0) return 'Not available';
    return `${Math.round(value)} ${unit}`;
  };

  // Helper function to get color based on performance thresholds
  const getMetricColor = (metric: string, value: number): string => {
    if (value === 0) return 'text-gray-400';
    
    switch (metric) {
      case 'lcp':
        return value < 2500 ? 'text-green-500' : value < 4000 ? 'text-yellow-500' : 'text-red-500';
      case 'fid':
        return value < 100 ? 'text-green-500' : value < 300 ? 'text-yellow-500' : 'text-red-500';
      case 'cls':
        return value < 0.1 ? 'text-green-500' : value < 0.25 ? 'text-yellow-500' : 'text-red-500';
      case 'ttfb':
        return value < 200 ? 'text-green-500' : value < 500 ? 'text-yellow-500' : 'text-red-500';
      case 'fcp':
        return value < 1800 ? 'text-green-500' : value < 3000 ? 'text-yellow-500' : 'text-red-500';
      default:
        return 'text-gray-400';
    }
  };

  return (
    <div className="fixed bottom-4 right-4 z-50 bg-gray-800 bg-opacity-90 text-white p-3 rounded-lg shadow-lg text-sm">
      <div className="flex justify-between items-center mb-2">
        <h3 className="font-bold">Performance Metrics</h3>
        <button 
          onClick={() => setVisible(false)}
          className="text-gray-400 hover:text-white"
        >
          ✕
        </button>
      </div>
      <table className="w-full">
        <tbody>
          <tr>
            <td>LCP:</td>
            <td className={getMetricColor('lcp', metrics.lcp)}>
              {formatMetric(metrics.lcp, 'ms')}
            </td>
          </tr>
          <tr>
            <td>FID:</td>
            <td className={getMetricColor('fid', metrics.fid)}>
              {formatMetric(metrics.fid, 'ms')}
            </td>
          </tr>
          <tr>
            <td>CLS:</td>
            <td className={getMetricColor('cls', metrics.cls)}>
              {metrics.cls === 0 ? 'Not available' : metrics.cls.toFixed(3)}
            </td>
          </tr>
          <tr>
            <td>TTFB:</td>
            <td className={getMetricColor('ttfb', metrics.ttfb)}>
              {formatMetric(metrics.ttfb, 'ms')}
            </td>
          </tr>
          <tr>
            <td>FCP:</td>
            <td className={getMetricColor('fcp', metrics.fcp)}>
              {formatMetric(metrics.fcp, 'ms')}
            </td>
          </tr>
        </tbody>
      </table>
      <div className="text-xs text-gray-400 mt-2">
        Press Ctrl+Shift+P to toggle
      </div>
    </div>
  );
};

export default PerformanceMonitor;
